import {
  checkFilterQuerySync,
  FilterQuery,
  FilterQueryParser,
  synthesizeAggregateProviders,
} from '@github-ui/filter-core'
import {
  type FilterConfig,
  type FilterProvider,
  type Parser,
  SubmitEvent,
  type SuppliedFilterProvider,
  type ValuePresenceFilterType,
} from '@github-ui/filter-core'
import {useClickAnalytics} from '@github-ui/use-analytics'
import {useSyncedState} from '@github-ui/use-synced-state'
import {createContext, use, useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {useFilter} from './RootContext'

interface FilterQueryState {
  filterQuery: FilterQuery
  /**
   * State mirror of `rawFilterRef.current`. Use this for render-time desync checks instead of
   * reading the ref — it stays in sync with the ref (updated at the same call sites) but is
   * safe to read during render under react-compiler.
   */
  latestRawFilter: string
  /**
   * Resync internal state when the external `rawFilter` prop diverges. Intentionally kept in the
   * (per-keystroke) state context rather than the stable dispatch context: its identity changes
   * with `rawFilter`, which is the mechanism the suggestions effect relies on to re-run and pick
   * up external filter changes.
   */
  updateFromExternal: (callback: (query: FilterQuery) => void | Promise<void>) => void
}

interface FilterQueryDispatch {
  clearFilter: () => void
  forceReparse: (cursor?: number, callback?: (query: FilterQuery) => void | Promise<void>) => void
  insertIntoQuery: (value: string, caretIndex: number, triggerSubmit?: boolean) => void
  onSubmit: (eventType: SubmitEvent, analyticsName?: string, updatedQuery?: FilterQuery) => void
  rawFilterRef: React.RefObject<string>
  replaceActiveBlockWithPresenceBlock: (blockType: ValuePresenceFilterType) => void
  updateFilter: (
    unparsedFilter?: string,
    caret?: number,
    callback?: (query: FilterQuery) => void,
    eventType?: SubmitEvent,
  ) => void
}

/**
 * Shallow reference-equality for two arrays: same length and identical element references.
 * Used to keep the `providers` array reference stable across renders when a caller passes an
 * inline literal (e.g. `providers={[]}`) whose contents have not actually changed.
 */
function providersArrayEqual(a: SuppliedFilterProvider[], b: SuppliedFilterProvider[]): boolean {
  return a === b || (a.length === b.length && a.every((provider, index) => provider === b[index]))
}

/**
 * Shallow value-equality for two static-context records: same keys and identical string values
 * (with `undefined` treated as equal to `undefined`). Used so the render-phase `staticContext`
 * guard only rebuilds `filterQuery` when the context contents actually change — not when a caller
 * passes a fresh `context={{...}}` object literal whose contents are unchanged.
 */
function staticContextEqual(a?: Record<string, string>, b?: Record<string, string>): boolean {
  if (a === b) return true
  if (!a || !b) return false
  const aKeys = Object.keys(a)
  if (aKeys.length !== Object.keys(b).length) return false
  return aKeys.every(key => a[key] === b[key])
}

export const FilterQueryStateContext = createContext<FilterQueryState | undefined>(undefined)
export const FilterQueryDispatchContext = createContext<FilterQueryDispatch | undefined>(undefined)
export const FilterProvidersContext = createContext<Record<string, FilterProvider> | undefined>(undefined)
export const FilterEmptyContext = createContext<boolean | undefined>(undefined)

export const useFilterQueryState = () => {
  const context = use(FilterQueryStateContext)
  if (!context) {
    throw new Error('useFilterQueryState must be used inside a FilterQueryStateContext')
  }

  return context
}

export const useFilterQueryDispatch = () => {
  const context = use(FilterQueryDispatchContext)
  if (!context) {
    throw new Error('useFilterQueryDispatch must be used inside a FilterQueryDispatchContext')
  }

  return context
}

export const useFilterProviders = () => {
  const context = use(FilterProvidersContext)
  if (!context) {
    throw new Error('useFilterProviders must be used inside a FilterProvidersContext')
  }

  return context
}

export const useFilterEmpty = () => {
  const context = use(FilterEmptyContext)
  if (context === undefined) {
    throw new Error('useFilterEmpty must be used inside a FilterEmptyContext')
  }

  return context
}

interface FilterQueryContextProviderProps {
  children: React.ReactNode
  context?: Record<string, string>
  customParser?: Parser<FilterQuery>
  filterConfig: FilterConfig
  providers: SuppliedFilterProvider[]
  inputRef: React.RefObject<HTMLInputElement | null>
  onChange: (value: string) => void
  onParse?: (request: FilterQuery) => void
  onSubmit?: (request: FilterQuery, eventType: SubmitEvent) => void
  onValidation: (messages: string[], filterQuery: FilterQuery) => void
  rawFilter: string
}

export const FilterQueryContextProvider = ({
  children,
  context: externalContext,
  customParser,
  filterConfig,
  providers: externalProviders,
  inputRef,
  onChange,
  onParse,
  onSubmit,
  onValidation,
  rawFilter,
}: FilterQueryContextProviderProps) => {
  const latestRawFilterRef = useRef(rawFilter ?? '')
  const [latestRawFilter, setLatestRawFilter] = useState(rawFilter ?? '')

  // Stabilize the providers array reference. Callers frequently pass an inline literal (e.g.
  // `providers={[]}` or `providers={[a, b]}`) which changes identity on every render. Without
  // this guard, `filterProviders` below would change identity each render, which in turn makes
  // the render-phase parser-rebuild check fire and drop the parser's internal parse/validate
  // caches on hot paths like typing. `useSyncedState` only adopts a new array when its contents
  // (element references) actually change, and returns the fresh value within the same render —
  // so when providers do change, `filterProviders` and the parser-rebuild guard below both see
  // the new array in one pass instead of cascading across extra render restarts.
  const [stableProviders] = useSyncedState(externalProviders, {isEqual: providersArrayEqual})

  const filterProviders = useMemo<Record<string, FilterProvider>>(
    () => synthesizeAggregateProviders(stableProviders),
    [stableProviders],
  )

  // Build a parser instance from the current props. When constructing our own parser, the full
  // memoized providers record (including synthesized `no` / `has` aggregate providers) is passed
  // to the constructor so no post-construction mutation is needed.
  //
  // When a `customParser` is supplied, it is returned as-is. The caller is responsible for
  // synthesizing aggregate providers themselves — see `synthesizeAggregateProviders` in
  // `@github-ui/filter-core`. We can no longer mutate `customParser` here (it's a hook arg).
  const buildParser = (): Parser<FilterQuery> => {
    if (customParser) return customParser
    return new FilterQueryParser(Object.values(filterProviders), filterConfig)
  }

  // Parser is stored in state so it can be safely read during render (no ref reads in render).
  // When the provider list changes we replace the parser entirely instead of mutating its
  // `filterProviders` field on a parser React already knows about. This loses the parser's
  // internal parse/validate caches on a provider change — acceptable because (a) provider lists
  // are typically static after mount and (b) cached validations may not be valid for a new
  // provider set anyway.
  const [parser, setParser] = useState<Parser<FilterQuery>>(buildParser)
  const [filterQuery, setFilterQuery] = useState<FilterQuery>(() => {
    // Seed the parse with externalContext so the very first filterQuery carries it; this avoids
    // an immediate set-state-during-render rebuild on mount.
    const starter = new FilterQuery('', [], filterConfig, undefined, {staticContext: externalContext})
    return parser.parse(rawFilter, starter, -1)
  })

  // Keep the parser in sync with the latest filterProviders. When the memoized providers
  // record changes identity, replace the parser entirely (immutable swap via setState during
  // render) so React owns the parser's lifecycle. The guard prevents an infinite loop once the
  // tracked providers match.
  const [lastSeenProviders, setLastSeenProviders] = useState(filterProviders)
  if (lastSeenProviders !== filterProviders) {
    setLastSeenProviders(filterProviders)
    setParser(buildParser())
  }

  // When externalContext changes, rebuild filterQuery with the new staticContext (immutable
  // replacement, not in-place mutation). Set state during render with a guard — React cancels
  // the current render and starts a new one, avoiding an extra commit that a useEffect-based
  // update would cause, and the guard prevents an infinite loop once staticContext matches.
  //
  // The guard compares by *value* (shallow record equality), not reference: callers frequently
  // pass an inline `context={{...}}` literal that changes identity every render, and a reference
  // check would force a needless rebuild each time. Unlike `providers`, `externalContext` isn't a
  // memo/effect dependency anywhere, so a value-comparing guard is sufficient — no stabilized
  // reference (e.g. `useSyncedState`) is needed.
  if (!staticContextEqual(filterQuery.staticContext, externalContext)) {
    setFilterQuery(
      prev =>
        new FilterQuery(
          prev.raw,
          prev.blocks,
          filterConfig,
          prev.activeBlock,
          {...prev.instanceContext, staticContext: externalContext},
          prev.isValidated,
        ),
    )
  }
  const [isInitialValidation, setIsInitialValidation] = useState(true)
  const {inputContextRef, config} = useFilter()

  const variant =
    config.variant === 'full' ? 'button_and_input' : config.variant === 'input' ? 'input_only' : 'button_only'

  const {sendClickAnalyticsEvent} = useClickAnalytics()

  // Latest volatile values, kept in a ref so the dispatch callbacks below can stay referentially
  // stable across renders (so the dispatch context never re-publishes on keystrokes). The ref is
  // seeded at init and synced in an effect — never written during render — so the React Compiler
  // can compile this provider (render-phase ref writes bail with "Cannot access refs during
  // render"). The dispatch callbacks only run from event handlers / effects (well after commit),
  // so reading last-committed values from the ref is behaviourally identical to closing over them.
  // This mirrors the existing `suggestionSelectActionsRef` pattern in SuggestionsContext.
  const latestRef = useRef({
    filterConfig,
    filterQuery,
    onChange,
    onParse,
    onSubmit,
    onValidation,
    parser,
    rawFilter,
    sendClickAnalyticsEvent,
    variant,
  })
  useEffect(() => {
    latestRef.current = {
      filterConfig,
      filterQuery,
      onChange,
      onParse,
      onSubmit,
      onValidation,
      parser,
      rawFilter,
      sendClickAnalyticsEvent,
      variant,
    }
  })

  const clearFilter = useCallback(() => {
    const {onChange: onChangeLatest, onSubmit: onSubmitLatest} = latestRef.current
    onChangeLatest('')
    onSubmitLatest?.(new FilterQuery(), SubmitEvent.Clear)
    inputRef.current?.focus()
  }, [inputRef])

  const postQueryValidate = useCallback(
    (request: FilterQuery, showAllErrors = false) => {
      const {filterConfig: filterConfigLatest, onValidation: onValidationLatest} = latestRef.current
      onValidationLatest(
        request.getErrors(showAllErrors || document.activeElement !== inputRef.current),
        new FilterQuery(request.raw, request.blocks, filterConfigLatest, request.activeBlock),
      )
    },
    [inputRef],
  )

  const parseAndValidate = useCallback((caret: number, callback?: (query: FilterQuery) => void, newRaw?: string) => {
    const {filterQuery: filterQueryLatest, parser: parserLatest, rawFilter: rawFilterLatest} = latestRef.current
    setIsInitialValidation(false)
    new Promise<FilterQuery>((resolve, reject) => {
      const query = parserLatest.parse(newRaw ?? rawFilterLatest, filterQueryLatest, caret)
      if (checkFilterQuerySync(query, latestRawFilterRef.current)) {
        // Intentionally do NOT commit this intermediate parse result. The parsed-but-unvalidated
        // blocks carry no error/valid styling, so they render identically to the raw text that
        // StyledInput already paints synchronously on keystroke (via its out-of-sync fallback).
        // Committing here would just trigger a redundant styled-tree rebuild that is immediately
        // superseded by the validation commit below — so we skip straight to validation and let
        // it be the single styled commit per keystroke. We still resolve so the chain proceeds.
        resolve(query)
      } else {
        reject(new Error('Query out of sync, Aborted...'))
      }
    })
      // * While we do want to use async/await, in this case it actually results in a broken experience as it blocks
      // * the UI from updating
      // eslint-disable-next-line github/no-then
      .then((query: FilterQuery) => parserLatest.validateFilterQuery(query))
      // eslint-disable-next-line github/no-then
      .then((newQuery: FilterQuery) => {
        return new Promise<FilterQuery>((resolve, reject) => {
          if (newQuery && checkFilterQuerySync(newQuery)) {
            setFilterQuery(newQuery)
            return resolve(newQuery)
          }
          return reject(new Error(newQuery ? 'Out of sync' : 'Empty Query, Aborted...'))
        })
      })
      // eslint-disable-next-line github/no-then
      .then((query: FilterQuery) => {
        callback?.(query)
      })
      // eslint-disable-next-line github/no-then
      .catch(() => {})
  }, [])

  const forceReparse = useCallback(
    (cursorLocation?: number, callback?: (query: FilterQuery) => void) => {
      const caret = cursorLocation ? cursorLocation : (inputContextRef.current?.caretStart ?? -1)
      parseAndValidate(caret, query => {
        postQueryValidate(query)
        callback?.(query)
      })
    },
    [inputContextRef, parseAndValidate, postQueryValidate],
  )

  const onSubmitHandler = useCallback(
    (eventType: SubmitEvent, analyticsName?: string, updatedQuery?: FilterQuery): void => {
      const {
        filterQuery: filterQueryLatest,
        onSubmit: onSubmitLatest,
        parser: parserLatest,
        rawFilter: rawFilterLatest,
        sendClickAnalyticsEvent: sendClickAnalyticsEventLatest,
        variant: variantLatest,
      } = latestRef.current

      const rawValueForSync = inputRef.current?.value ?? rawFilterLatest

      // Submit runs synchronously in the same task as the triggering event (e.g. Enter). The
      // last-committed `filterQueryLatest` can lag the live input by one validation cycle
      // (validation runs off each keystroke and commits in a later task, and the ref that exposes
      // it is synced in a passive effect after commit), so use it only when it's already in sync;
      // otherwise synchronously re-parse the *internal* raw value (`latestRawFilterRef`, updated
      // synchronously as the query changes) — NOT the live DOM value. parse() is synchronous, so
      // for a transient validation lag the re-parsed query matches the DOM and we submit in the
      // same task, restoring the synchronous submit behaviour that existed before the dispatch
      // context was made referentially stable.
      const query =
        updatedQuery ??
        (checkFilterQuerySync(filterQueryLatest, rawValueForSync)
          ? filterQueryLatest
          : parserLatest.parse(
              latestRawFilterRef.current,
              filterQueryLatest,
              inputContextRef.current?.caretStart ?? -1,
            ))

      // Clearing the active block so validation errors will show
      query.clearActiveBlock()

      if (checkFilterQuerySync(query, rawValueForSync)) {
        onSubmitLatest?.(query, eventType)
        postQueryValidate(query, true)
      } else {
        // The synchronously derived query is still out of sync with the live input. This only
        // happens for a genuine, persistent desync (e.g. an externally-controlled `filterValue`
        // the DOM has diverged from). Kick off a fresh parse + validate and submit only if it
        // resolves to a query that matches the input; otherwise we intentionally do not submit.
        parseAndValidate(inputContextRef.current?.caretStart ?? -1, q => {
          if (checkFilterQuerySync(q, rawValueForSync)) {
            onSubmitLatest?.(q, eventType)
            postQueryValidate(query, true)
          }
        })
      }

      const filtersUsed = filterQueryLatest.filtersUsed.length > 0 ? filterQueryLatest.filtersUsed.join(',') : ''

      sendClickAnalyticsEventLatest({
        action: 'submit_filter_query',
        label: `variant:${variantLatest};event_type:${eventType};used_filter_providers:${filtersUsed};nested_group_depth:${
          filterQueryLatest.nestedGroupCount
        };submitted_by:${analyticsName ?? 'unnamed_submit_method'}`,
      })
    },
    [inputContextRef, inputRef, parseAndValidate, postQueryValidate],
  )

  const updateFilter = useCallback(
    (
      unparsedFilter?: string,
      caret: number = -1,
      callback?: (query: FilterQuery) => void | null,
      eventType?: SubmitEvent,
    ) => {
      const {
        filterQuery: filterQueryLatest,
        onChange: onChangeLatest,
        onParse: onParseLatest,
        onSubmit: onSubmitLatest,
      } = latestRef.current
      const updatedRaw = unparsedFilter ?? filterQueryLatest.raw

      onChangeLatest?.(updatedRaw)
      if (eventType) {
        latestRawFilterRef.current = updatedRaw
        setLatestRawFilter(updatedRaw)
        parseAndValidate(
          caret,
          query => {
            if (checkFilterQuerySync(query, latestRawFilterRef.current)) {
              callback?.(query)
              onSubmitLatest?.(query, eventType)
              onParseLatest?.(query)
            }
          },
          updatedRaw,
        )
      }
    },
    [parseAndValidate],
  )

  const replaceActiveBlockWithPresenceBlock = useCallback(
    (type: ValuePresenceFilterType) => {
      const {filterQuery: filterQueryLatest, parser: parserLatest} = latestRef.current
      const [raw, cursorIndex] = parserLatest.replaceActiveBlockWithPresenceBlock(filterQueryLatest, type)
      inputContextRef.current?.updateCaretPosition(cursorIndex)
      updateFilter(raw, cursorIndex)
    },
    [inputContextRef, updateFilter],
  )

  const insertIntoQuery = useCallback(
    (value: string, caretIndex: number, triggerSubmit: boolean = false) => {
      const {filterQuery: filterQueryLatest, parser: parserLatest} = latestRef.current
      const [raw, cursorIndex] = parserLatest.insertSuggestion(filterQueryLatest, value, caretIndex)
      inputContextRef.current?.updateCaretPosition(cursorIndex)
      // Insert the updated raw query into the input context
      inputContextRef.current?.updateRawFilterValue(raw)
      updateFilter(raw, cursorIndex, undefined, triggerSubmit ? SubmitEvent.SuggestionSelected : undefined)
    },
    [inputContextRef, updateFilter],
  )

  // Kept volatile (closes over `rawFilter` / `isInitialValidation`) and published via the state
  // context — NOT the stable dispatch context. The suggestions effect depends on this callback's
  // identity to re-run when the external `rawFilter` prop changes; stabilizing it would silently
  // break external-filter resync. `rawFilter` is passed to `parseAndValidate` explicitly so the
  // parse uses the current external value rather than the last-committed one in `latestRef`.
  const updateFromExternal = useCallback(
    (callback: (query: FilterQuery) => void) => {
      if (rawFilter !== latestRawFilterRef.current || isInitialValidation) {
        latestRawFilterRef.current = rawFilter
        setLatestRawFilter(rawFilter)
        parseAndValidate(
          inputContextRef.current?.caretStart ?? -1,
          query => {
            postQueryValidate(query)
            onParse?.(query)
            callback(query)
          },
          rawFilter,
        )
      }
    },
    [inputContextRef, isInitialValidation, onParse, parseAndValidate, postQueryValidate, rawFilter],
  )

  const isFilterEmpty = latestRawFilter.length === 0

  const filterQueryStateValue = useMemo<FilterQueryState>(
    () => ({filterQuery, latestRawFilter, updateFromExternal}),
    [filterQuery, latestRawFilter, updateFromExternal],
  )

  const filterQueryDispatchValue = useMemo<FilterQueryDispatch>(
    () => ({
      clearFilter,
      forceReparse,
      insertIntoQuery,
      onSubmit: onSubmitHandler,
      rawFilterRef: latestRawFilterRef,
      replaceActiveBlockWithPresenceBlock,
      updateFilter,
    }),
    [clearFilter, forceReparse, insertIntoQuery, onSubmitHandler, replaceActiveBlockWithPresenceBlock, updateFilter],
  )

  return (
    <FilterProvidersContext value={filterProviders}>
      <FilterQueryDispatchContext value={filterQueryDispatchValue}>
        <FilterEmptyContext value={isFilterEmpty}>
          <FilterQueryStateContext value={filterQueryStateValue}>{children}</FilterQueryStateContext>
        </FilterEmptyContext>
      </FilterQueryDispatchContext>
    </FilterProvidersContext>
  )
}
